import React, { useEffect, useRef, useState } from "react";

import Iconfont from "@components/iconfont/iconfont";
import MarkdownRenderer from "@components/markdown_renderer";
import NewsCardCover from "@components/news_card_cover";

import { formatNewsPublishDate } from "../../shared/news";
import BackTopButton from "../../shared/BackTopButton";
import NewsDetailHeader from "../../shared/NewsDetailHeader";
import useNewsDetail from "../../shared/useNewsDetail";
import useNewsRecommend from "../../shared/useNewsRecommend";

import "./MobileNewsDetail.less";

/**
 * 资讯详情 / Mobile
 *
 * 顶部固定 nav 由 mobile/App.jsx 常驻；本组件内是面包屑 + 单列正文。
 * 正文渲染逻辑与 Web 端一致：有内容走 MarkdownRenderer，加载中留空，
 * 失败 / 无内容显示占位文案。
 *
 * @param {string} id 资讯 id
 * @param {string} title 列表卡片标题，详情接口返回前先展示
 * @param {Function} onBack 返回资讯列表
 * @param {Function} onOpenDetail 点击上一篇 / 下一篇，进入另一篇详情
 */
export default function MobileNewsDetail({
  id,
  title = "",
  onBack,
  onOpenDetail,
}) {
  const { detail, loading, error } = useNewsDetail(id);
  const { latest } = useNewsRecommend(id);
  /** 一条有效数据都没有时，最新内容推荐整块（含标题）不渲染 */
  const visibleLatest = latest.filter((item) => item && item.id && item.title);
  const content = String(detail?.article?.content || "");
  const titleText = String(detail?.article?.title || title || "").trim();
  const prev = detail?.prev || null;
  const next = detail?.next || null;
  const hasPrev = Boolean(detail) && !detail?.prevEnd && Boolean(prev);
  const hasNext = Boolean(detail) && !detail?.nextEnd && Boolean(next);
  const showPager = Boolean(detail);
  const detailState = loading
    ? "loading"
    : error
      ? "error"
      : detail
        ? "success"
        : "idle";

  const openPagerItem = (article) => {
    if (!article) return;
    onOpenDetail?.(article.id, article.title);
  };

  /* 页尾「获取服务支持」区块：与列表页「到底提示出现就让位」同一规则 ——
     这块一进入视口，悬浮回顶按钮就隐藏，否则会正好压在 CTA / footer 链接上。 */
  const supportRef = useRef(null);
  const [supportVisible, setSupportVisible] = useState(false);

  useEffect(() => {
    const node = supportRef.current;
    if (!node || typeof IntersectionObserver !== "function") return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[entries.length - 1];
        if (entry) setSupportVisible(entry.isIntersecting);
      },
      { threshold: 0 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  /** 与 Web / 「立刻获取方案」一致：问卷新标签打开 */
  const handleSupportOpen = (event) => {
    event.preventDefault();
    const target = event.currentTarget;
    target.classList.add("is-pressed");
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        window.open(
          "https://v.wjx.cn/vm/PD6aaEY.aspx#",
          "_blank",
          "noopener,noreferrer"
        );
        setTimeout(() => target.classList.remove("is-pressed"), 200);
      })
    );
  };

  return (
    <div
      className="m-news-detail"
      data-news-id={id || ""}
      data-news-state={detailState}
    >
      <nav className="m-news-detail-breadcrumb" aria-label="面包屑">
        <div className="m-news-detail-breadcrumb__inner">
          <button
            type="button"
            className="m-news-detail-breadcrumb__link"
            onClick={onBack}
          >
            资讯
          </button>
          <Iconfont
            type="icon-a-danjiantouxiangyouxi"
            className="m-news-detail-breadcrumb__icon"
          />
          <span
            className="m-news-detail-breadcrumb__title"
            title={titleText}
          >
            {titleText}
          </span>
        </div>
      </nav>

      <div className="m-news-detail__content">
        <NewsDetailHeader article={detail?.article} />
        {content.trim() ? (
          <MarkdownRenderer
            markdown={content}
            className="m-news-detail-markdown"
          />
        ) : loading ? null : (
          <div className="m-news-detail__placeholder">
            {error ? "资讯内容加载失败" : "暂无内容"}
          </div>
        )}
      </div>

      {showPager ? (
        <div className="m-news-detail-pager">
          <div className="m-news-detail-pager__row">
            <button
              type="button"
              className={`m-news-detail-pager__item${
                hasPrev ? "" : " m-news-detail-pager__item--disabled"
              }`}
              disabled={!hasPrev}
              onClick={() => openPagerItem(prev)}
            >
              <Iconfont
                type="icon-a-danjiantouxiangzuoxi"
                className="m-news-detail-pager__icon"
              />
              <span className="m-news-detail-pager__label">上一篇</span>
            </button>

            <span
              className="m-news-detail-pager__divider"
              aria-hidden="true"
            />

            <button
              type="button"
              className={`m-news-detail-pager__item${
                hasNext ? "" : " m-news-detail-pager__item--disabled"
              }`}
              disabled={!hasNext}
              onClick={() => openPagerItem(next)}
            >
              <span className="m-news-detail-pager__label">下一篇</span>
              <Iconfont
                type="icon-a-danjiantouxiangyouxi"
                className="m-news-detail-pager__icon"
              />
            </button>
          </div>
        </div>
      ) : null}

      {visibleLatest.length > 0 ? (
        <section className="m-news-detail-latest" aria-label="最新内容推荐">
          <div className="m-news-detail-latest__head">
            <span className="m-news-detail-latest__eyebrow">RELATED</span>
            <span className="m-news-detail-latest__heading">最新内容推荐</span>
          </div>

          <div className="m-news-detail-latest__list">
            {visibleLatest.map((item) => {
              const meta = [
                item.categoryLabel,
                formatNewsPublishDate(item.tmPublished),
              ]
                .filter(Boolean)
                .join("：");

              return (
                <button
                  key={item.id}
                  type="button"
                  className="m-news-detail-latest__item"
                  onClick={() => onOpenDetail?.(item.id, item.title)}
                >
                  <span className="m-news-detail-latest__cover">
                    <NewsCardCover
                      coverUrl={item.cover || ""}
                      title={item.title || ""}
                    />
                  </span>

                  <span className="m-news-detail-latest__info">
                    <span className="m-news-detail-latest__title">
                      {item.title}
                    </span>
                    {meta ? (
                      <span className="m-news-detail-latest__meta">
                        {meta}
                      </span>
                    ) : null}
                  </span>
                </button>
              );
            })}
          </div>
        </section>
      ) : null}

      <section className="m-news-detail-support" aria-label="获取服务支持" ref={supportRef}>
        <div className="m-news-detail-support__inner">
          <div className="m-news-detail-support__content">
            <span className="m-news-detail-support__eyebrow">
              BUJUE · CONTENT
            </span>
            <p className="m-news-detail-support__title">
              让好内容，持续创造价值
            </p>
            <p className="m-news-detail-support__desc">
              发现更多关于品牌、产品与创造力的思考。
            </p>
          </div>

          <a
            href="https://v.wjx.cn/vm/PD6aaEY.aspx#"
            className="m-news-detail-support__cta"
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleSupportOpen}
          >
            获取服务支持
          </a>
        </div>
      </section>

      {/* 与列表页同一个悬浮按钮：页面不是滚到最顶就出现，点一下回顶部；
          页尾「获取服务支持」进入视口后让位（同列表页「到底提示」规则） */}
      <BackTopButton hidden={supportVisible} />
    </div>
  );
}
